;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();import { useEffect, useState } from 'react';

import { assetFragment } from 'lib/graphql/fragments';

import { graphRequest } from '../services/contentful';

// GraphQL query to fetch all favorites at once using id_in filter
const FAVORITES_QUERY = /* GraphQL */ `
    ${assetFragment}
    query GetFavorites($ids: [String!]!) {
        entryCollection(where: { sys: { id_in: $ids } }) {
            items {
                sys {
                    id
                }
                __typename
                ... on Product {
                    displayName
                    slug
                    category {
                        title
                        slug
                    }
                    discontinued
                    textColor
                    color
                    eyebrow
                    icon
                    award {
                        json
                    }
                    awardAsset {
                        ...Asset
                    }
                    image {
                        url
                        title
                        description
                        width
                        height
                    }
                }
                ... on Recipe {
                    title
                    slug
                    cookTime
                    prepTime
                    serves
                    category {
                        title
                        slug
                    }
                    cardImage {
                        url
                        description
                    }
                    cardImageLarge {
                        url
                    }
                    cardImage {
                        url
                        title
                        description
                    }
                    images: imagesCollection {
                        items {
                            url
                            title
                            description
                        }
                    }
                    image {
                        url
                        title
                        description
                    }
                }
            }
        }
    }
`;

/**
 * Custom hook to fetch Contentful data for favorited items using optimized GraphQL
 * @param {Array} favorites - Array of favorite items with id and type
 * @returns {Object} - { contentfulData, isLoading, error }
 */
export const useContentfulFavorites = favorites => {
    const [contentfulData, setContentfulData] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!favorites || favorites.length === 0) {
            setContentfulData({});
            setIsLoading(false);
            setError(null);
            return;
        }

        const fetchContentfulData = async () => {
            setIsLoading(true);
            setError(null);

            try {
                // Extract all favorite IDs for the GraphQL query, filtering out null/invalid values
                const favoriteIds = favorites
                    .map(favorite => favorite.id)
                    .filter(id => id && typeof id === 'string');

                if (favoriteIds.length === 0) {
                    setIsLoading(false);
                    return;
                }

                // Make single GraphQL request for all favorites
                const { body } = await graphRequest({
                    query: FAVORITES_QUERY,
                    variables: { ids: favoriteIds },
                });

                const entries = body?.data?.entryCollection?.items || [];
                const newContentfulData = {};

                // Process each entry and map it back to favorites
                entries.forEach(entry => {
                    if (!entry || !entry.sys?.id) return;

                    // Find the corresponding favorite to get the type
                    const favorite = favorites.find(
                        fav => fav.id === entry.sys.id
                    );
                    if (!favorite) return;

                    const key = `${favorite.type}-${favorite.id}`;

                    newContentfulData[key] = entry;
                });

                // Handle favorites that weren't found in Contentful
                favorites.forEach(favorite => {
                    const key = `${favorite.type}-${favorite.id}`;
                    if (!newContentfulData[key]) {
                        console.warn(
                            `No Contentful entry found for ${favorite.type}:${favorite.id}`
                        );
                        // Keep the original favorite data if Contentful fetch fails
                        newContentfulData[key] = {
                            ...favorite,
                            contentfulError: 'Entry not found',
                        };
                    }
                });

                setContentfulData(newContentfulData);
            } catch (error) {
                console.warn(
                    'Error fetching Contentful data for favorites:',
                    error
                );
                setError(error.message || 'Failed to fetch content');

                // Fallback: use original favorite data
                const fallbackData = {};
                favorites.forEach(favorite => {
                    const key = `${favorite.type}-${favorite.id}`;
                    fallbackData[key] = {
                        ...favorite,
                        contentfulError: 'Fetch failed',
                    };
                });
                setContentfulData(fallbackData);
            } finally {
                setIsLoading(false);
            }
        };

        fetchContentfulData();
    }, [favorites]);

    return { contentfulData, isLoading, error };
};

/**
 * Helper function to get enhanced favorite data with Contentful content
 * @param {Object} favorite - Original favorite item
 * @param {Object} contentfulData - Contentful data object
 * @returns {Object} - Enhanced favorite with latest Contentful data
 */
export const getEnhancedFavorite = (favorite, contentfulData) => {
    const key = `${favorite.type}-${favorite.id}`;
    const contentfulItem = contentfulData[key];

    if (!contentfulItem) {
        return favorite; // Return original if no Contentful data
    }

    return {
        ...contentfulItem, // Override with fresh Contentful data
        // Ensure we keep the original favorite type and ID
        type: favorite.type,
        id: favorite.id,
        favoritedAt: favorite.favoritedAt,
    };
};
